/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { memo, useMemo } from 'react';

import { customizeStore } from '@/stores';
import { renderCustomSlot, CustomSlotVars } from '@/utils/customSlot';

type SlotName = 'question' | 'answer' | 'answer_accepted';

const FIELD_BY_SLOT: Record<
  SlotName,
  keyof ReturnType<typeof customizeStore.getState>
> = {
  question: 'custom_question_slot',
  answer: 'custom_answer_slot',
  answer_accepted: 'custom_answer_accepted_slot',
};

interface CustomSlotProps {
  slot: SlotName;
  vars?: CustomSlotVars;
}

const CustomSlot = ({ slot, vars }: CustomSlotProps) => {
  const field = FIELD_BY_SLOT[slot];
  const html = customizeStore((state) => state[field]) as string;
  const rendered = useMemo(
    () => renderCustomSlot(html, vars ?? {}),
    [html, vars],
  );
  if (!rendered) return null;
  return (
    <div
      className={`custom-slot custom-slot-${slot}`}
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
};

export default memo(CustomSlot);
